/**
 * postMessage protocol between the embeddable widget (host page) and the
 * `/embed` route running inside the overlay iframe.
 *
 * Both sides validate `event.origin` before trusting a message; these guards
 * only validate shape.
 */

export const PROTOCOL_VERSION = 1;

export type ThemeMode = 'light' | 'dark';

/** Sent once the frame reports ready, and again whenever host config changes. */
export interface EsInitMessage {
  type: 'es:init';
  version: number;
  query: string;
  filters: Record<string, string[]>;
  tokens: Record<string, string>;
  theme: ThemeMode;
  uiLanguage?: string;
  fontCssUrl?: string;
  overlayTitle?: string;
  overlayLogoUrl?: string;
  /** Host-origin search history, so the overlay's own bar shows the same list. */
  history: string[];
}

export interface EsQueryMessage {
  type: 'es:query';
  version: number;
  query: string;
  filters?: Record<string, string[]>;
}

export interface EsThemeMessage {
  type: 'es:theme';
  version: number;
  tokens: Record<string, string>;
  theme: ThemeMode;
}

export type HostToFrameMessage = EsInitMessage | EsQueryMessage | EsThemeMessage;

export interface EsReadyMessage {
  type: 'es:ready';
  version: number;
}

export interface EsCloseMessage {
  type: 'es:close';
  version: number;
}

/**
 * Public subset of a search result handed to the host page when a result is
 * clicked. Field names follow the search API. This is part of the host contract,
 * so only add fields — never rename or remove them.
 */
export interface EsResultSummary {
  id: string;
  source: string;
  source_type: string;
  parent_document_id?: string;
  title?: string;
  url?: string;
  language?: string;
  zenon_version?: string;
  document_version?: string;
  breadcrumb?: string[];
  /** 1-based rank across all pages. */
  position?: number;
}

export interface EsNavigateMessage {
  type: 'es:navigate';
  version: number;
  url: string;
  newTab?: boolean;
  /** Query the clicked result belongs to; absent from older frames. */
  query?: string;
  /** Absent for links that are not a search result, and from older frames. */
  result?: EsResultSummary;
}

export interface EsHistoryAddMessage {
  type: 'es:history-add';
  version: number;
  query: string;
}

export type FrameToHostMessage =
  | EsReadyMessage
  | EsCloseMessage
  | EsNavigateMessage
  | EsHistoryAddMessage;

const FRAME_TO_HOST_TYPES = new Set([
  'es:ready',
  'es:close',
  'es:navigate',
  'es:history-add',
]);

const HOST_TO_FRAME_TYPES = new Set(['es:init', 'es:query', 'es:theme']);

function hasVersionedType(data: unknown): data is { type: string; version: number } {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as { type?: unknown; version?: unknown };
  return typeof candidate.type === 'string' && candidate.version === PROTOCOL_VERSION;
}

export function isFrameToHostMessage(data: unknown): data is FrameToHostMessage {
  return hasVersionedType(data) && FRAME_TO_HOST_TYPES.has(data.type);
}

export function isHostToFrameMessage(data: unknown): data is HostToFrameMessage {
  return hasVersionedType(data) && HOST_TO_FRAME_TYPES.has(data.type);
}

const OPTIONAL_STRING_FIELDS = [
  'parent_document_id',
  'title',
  'url',
  'language',
  'zenon_version',
  'document_version',
] as const;

/**
 * Copy only the contracted fields with the expected types. Used on both sides:
 * the frame must not leak full documents to the host, and the host must not
 * pass arbitrary frame-supplied objects on to its own listeners.
 */
export function toResultSummary(data: unknown): EsResultSummary | null {
  if (typeof data !== 'object' || data === null) return null;
  const source = data as Record<string, unknown>;
  if (
    typeof source.id !== 'string' ||
    typeof source.source !== 'string' ||
    typeof source.source_type !== 'string'
  ) {
    return null;
  }

  const summary: EsResultSummary = {
    id: source.id,
    source: source.source,
    source_type: source.source_type,
  };
  for (const field of OPTIONAL_STRING_FIELDS) {
    const value = source[field];
    if (typeof value === 'string') summary[field] = value;
  }
  if (Array.isArray(source.breadcrumb)) {
    summary.breadcrumb = source.breadcrumb.filter(
      (entry): entry is string => typeof entry === 'string'
    );
  }
  if (typeof source.position === 'number' && Number.isInteger(source.position)) {
    summary.position = source.position;
  }
  return summary;
}

/** Only http(s) may be opened from a frame-supplied URL — blocks `javascript:`. */
export function isSafeNavigationUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
